import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Type, RotateCcw, Check, ArrowUp, ArrowDown, ArrowLeft, ArrowRight } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import api, { formatApiError } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";

const loadFont = (family, weight) => {
  const id = `gf-${family}-${weight || "default"}`.replace(/\s+/g, "-");
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family)}${weight ? `:wght@${weight}` : ""}&display=swap`;
  document.head.appendChild(link);
};

const FontLine = ({ line, i, pick, setPick }) => {
  const [custom, setCustom] = useState("");
  useEffect(() => line.fonts.forEach((f) => loadFont(f.family, f.weight)), [line]);
  return (
    <div className="border border-black/10 bg-white p-4" data-testid={`real-fonts-line-${i}`}>
      <label className="flex items-center gap-2.5 mb-3 cursor-pointer select-none" htmlFor={`real-fonts-use-${i}`}>
        <Checkbox id={`real-fonts-use-${i}`} checked={pick.use} onCheckedChange={(v) => setPick({ ...pick, use: !!v })}
          data-testid={`real-fonts-use-${i}`} className="rounded-none data-[state=checked]:bg-[#ED5A24] data-[state=checked]:border-[#ED5A24]" />
        <span className="text-sm font-bold">{pick.use ? "Use a real font" : "Keep as is"}</span>
        <span className="text-xs text-[#52525B] truncate">“{line.text}”</span>
      </label>
      <div className={pick.use ? "" : "opacity-40 pointer-events-none"} data-testid={`real-fonts-options-${i}`}>
      <input value={pick.text} onChange={(e) => setPick({ ...pick, text: e.target.value })} data-testid={`real-fonts-text-${i}`}
        className="w-full border border-black/15 h-10 px-3 text-sm mb-3 focus:outline-none focus:border-[#ED5A24]" aria-label="Logo text" />
      <div className="grid grid-cols-2 gap-2">
        {line.fonts.map((f, j) => {
          const on = pick.use && pick.family === f.family && pick.weight === f.weight;
          return (
            <button key={f.family + f.weight} type="button" onClick={() => setPick({ ...pick, family: f.family, weight: f.weight })}
              data-testid={`real-fonts-option-${i}-${j}`}
              className={`text-left border px-3 py-2 transition-colors duration-200 ${on ? "border-[#ED5A24] bg-[#FDF0EA]" : "border-black/10 hover:border-[#0A0A0B]"}`}>
              <div className="text-lg truncate" style={{ fontFamily: `'${f.family}'`, fontWeight: f.weight }}>{pick.text}</div>
              <div className="text-[11px] text-[#52525B] flex items-center gap-1">{on && <Check className="w-3 h-3 text-[#ED5A24]" />}{f.family} {f.weight}</div>
            </button>
          );
        })}
      </div>
      {pick.use && !line.fonts.some((f) => f.family === pick.family) && (
        <div className="mt-2 border border-[#ED5A24] bg-[#FDF0EA] px-3 py-2" data-testid={`real-fonts-custom-active-${i}`}>
          <div className="text-lg truncate" style={{ fontFamily: `'${pick.family}'`, fontWeight: pick.weight }}>{pick.text}</div>
          <div className="text-[11px] text-[#52525B] flex items-center gap-1"><Check className="w-3 h-3 text-[#ED5A24]" />{pick.family} · your pick</div>
        </div>
      )}
      <Nudge pick={pick} setPick={setPick} i={i} />
      <div className="flex gap-2 mt-2">
        <input value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Or type any Google Font name" data-testid={`real-fonts-custom-${i}`}
          className="flex-1 border border-black/15 h-9 px-3 text-xs focus:outline-none focus:border-[#ED5A24]" />
        <button type="button" disabled={!custom.trim()} data-testid={`real-fonts-custom-use-${i}`}
          onClick={() => { loadFont(custom.trim(), null); setPick({ ...pick, family: custom.trim(), weight: 400 }); }}
          className="border border-black/15 hover:border-[#0A0A0B] px-3 text-xs font-bold disabled:opacity-40">Use</button>
      </div>
      </div>
    </div>
  );
};

const STEP = 0.1;
const NUDGES = [
  { key: "up", icon: ArrowUp, d: { dy: -STEP } }, { key: "down", icon: ArrowDown, d: { dy: STEP } },
  { key: "left", icon: ArrowLeft, d: { dx: -STEP } }, { key: "right", icon: ArrowRight, d: { dx: STEP } },
];
const clamp = (v) => Math.max(-3, Math.min(3, Math.round(v * 100) / 100));

const Nudge = ({ pick, setPick, i }) => (
  <div className="mt-3 flex flex-wrap items-center gap-3" data-testid={`real-fonts-nudge-${i}`}>
    <span className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#52525B]">Size</span>
    <Slider className="w-32" min={50} max={160} step={5} value={[Math.round(pick.scale * 100)]}
      onValueChange={([v]) => setPick({ ...pick, scale: v / 100 })} data-testid={`real-fonts-size-${i}`} />
    <span className="text-xs w-10" data-testid={`real-fonts-size-value-${i}`}>{Math.round(pick.scale * 100)}%</span>
    <div className="flex gap-1">
      {NUDGES.map((n) => (
        <button key={n.key} type="button" aria-label={`Move ${n.key}`} data-testid={`real-fonts-move-${n.key}-${i}`}
          onClick={() => setPick({ ...pick, dx: clamp(pick.dx + (n.d.dx || 0)), dy: clamp(pick.dy + (n.d.dy || 0)) })}
          className="w-8 h-8 border border-black/15 hover:border-[#ED5A24] hover:text-[#ED5A24] flex items-center justify-center transition-colors duration-200">
          <n.icon className="w-3.5 h-3.5" />
        </button>
      ))}
    </div>
    {(pick.scale !== 1 || !!pick.dx || !!pick.dy) && (
      <button type="button" onClick={() => setPick({ ...pick, scale: 1, dx: 0, dy: 0 })} data-testid={`real-fonts-reset-${i}`}
        className="text-xs text-[#ED5A24] underline underline-offset-4">Reset</button>
    )}
  </div>
);

export const RealFontsPanel = ({ result, onUpdated }) => {
  const [lines, setLines] = useState(null);
  const [picks, setPicks] = useState([]);
  const [busy, setBusy] = useState("");

  useEffect(() => { setLines(null); setPicks([]); }, [result.job_id]);

  const run = async (kind, fn) => {
    setBusy(kind);
    try { await fn(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong"); }
    finally { setBusy(""); }
  };

  const detect = () => run("detect", async () => {
    const { data } = await api.post(`/jobs/${result.job_id}/real-fonts/detect`);
    if (!data.lines.length) return toast.info("No text found in this logo");
    setLines(data.lines);
    setPicks(data.lines.map((l, i) => {
      const prev = data.applied?.find((a) => a.index === i);
      if (prev) return { scale: 1, dx: 0, dy: 0, ...prev, use: true };
      return { index: i, text: l.text, family: l.fonts[0].family, weight: l.fonts[0].weight, scale: 1, dx: 0, dy: 0, use: !data.applied };
    }));
  });

  const apply = () => run("apply", async () => {
    const choices = picks.filter((p) => p.use).map(({ use, ...p }) => p);
    if (!choices.length) return toast.info(result.real_fonts ? "Nothing ticked — tap Back to traced to keep everything as is" : "Tick at least one line to use a real font");
    const { data } = await api.post(`/jobs/${result.job_id}/real-fonts/apply`, { choices });
    onUpdated(data);
    toast.success("Real fonts applied — all files updated");
  });

  const revert = () => run("revert", async () => {
    const { data } = await api.post(`/jobs/${result.job_id}/real-fonts/revert`);
    onUpdated(data);
    toast.success("Back to the traced lettering");
  });

  return (
    <div className="mt-12 border border-black/10 bg-[#F8F9FA] p-6 md:p-8" data-testid="real-fonts-panel">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#52525B] mb-2 flex items-center gap-2"><Type className="w-3.5 h-3.5" /> Optional</div>
          <h2 className="text-2xl md:text-3xl tracking-tight font-bold">Use real fonts</h2>
          <p className="text-sm text-[#52525B] mt-2 max-w-xl">Not happy with how the lettering was recreated? Swap it for a matching real font. Switch any time, or go back to the traced version.</p>
        </div>
        <div className="flex gap-2">
          {result.real_fonts && (
            <button type="button" onClick={revert} disabled={!!busy} data-testid="real-fonts-revert-btn"
              className="inline-flex items-center gap-2 border border-black/20 hover:border-[#0A0A0B] text-xs uppercase tracking-[0.2em] font-bold h-11 px-5 disabled:opacity-50">
              {busy === "revert" ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />} Back to traced
            </button>
          )}
          {!lines && (
            <button type="button" onClick={detect} disabled={!!busy} data-testid="real-fonts-detect-btn"
              className="inline-flex items-center gap-2 bg-[#0A0A0B] hover:bg-[#ED5A24] text-white text-xs uppercase tracking-[0.2em] font-bold h-11 px-5 transition-colors duration-200 disabled:opacity-50">
              {busy === "detect" ? <><Loader2 className="w-4 h-4 animate-spin" /> Reading your logo…</> : "Find text & matching fonts"}
            </button>
          )}
        </div>
      </div>
      {lines && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {lines.map((l, i) => (
              <FontLine key={i} line={l} i={i} pick={picks[i]} setPick={(p) => setPicks((all) => all.map((x, k) => (k === i ? p : x)))} />
            ))}
          </div>
          <button type="button" onClick={apply} disabled={!!busy} data-testid="real-fonts-apply-btn"
            className="mt-4 inline-flex items-center gap-2 bg-[#ED5A24] hover:bg-[#0A0A0B] text-white text-xs uppercase tracking-[0.2em] font-bold h-12 px-6 transition-colors duration-200 disabled:opacity-50">
            {busy === "apply" ? <><Loader2 className="w-4 h-4 animate-spin" /> Rebuilding your logo…</> : "Use these fonts"}
          </button>
          <span className="ml-3 text-xs text-[#52525B]">Size and position changes show after you tap Use these fonts.</span>
        </>
      )}
    </div>
  );
};
